import {
  Container,
  styled,
  Box,
} from "@mui/material"
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import FormTextArea from "components/form/FormTextArea";
import FormSelect from "components/form/FormSelect";
import FormLabeledTextField from "components/form/FormLabeledTextField";
import StyledButton from "components/button/StyledButton";
import { useNavigate } from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import { ProjectContext } from "context/ProjectContext";
import "../../components/button/StyledButton.css"
import "./AddProjectSmqAssumptions.css"
import { useGetCarbonFactorDataSourcesQuery, useGetMaterialQuantityDataSourcesQuery, useGetUserQuery, useUpdateProjectMutation } from "api/api";
import { useCreateUpdateProject } from "hooks/useCreateUpdateProject";
import { useStepBackNavigation } from "hooks/useStepBackNavigation";
import FormTextField from "components/form/FormTextField";
import SaveErrorAlert from "components/form/SaveErrorAlert";
import RevertToDraftDialog from "components/form/RevertToDraftDialog";
import DiscardChangesDialog from "components/form/DiscardChangesDialog";
import { STEP_FIELDS } from "lib/utils/projectPatch";

const baseSchema = z.object({
  material_quantity_data_source: z.string({
    required_error: "Material quantity data source is required",
    invalid_type_error: "Material quantity data source is required"
  }),
  steel_connection_allowances: z.string().refine((val) => {
    if (val === '') return true;
    const num = parseFloat(val);
    return !isNaN(num) && num >= 0 && num <= 100;
  }, "Must be a number between 0 and 100").nullable(),
  rebar_allowances: z.string().refine((val) => {
    if (val === '') return true;
    const num = parseFloat(val);
    return !isNaN(num) && num >= 0 && num <= 100;
  }, "Must be a number between 0 and 100").nullable(),
  concrete_waste_allowances: z.string().refine((val) => {
    if (val === '') return true;
    const num = parseFloat(val);
    return !isNaN(num) && num >= 0 && num <= 100;
  }, "Must be a number between 0 and 100").nullable(),
  carbon_factor_data_source: z.string().nullable(),
  other_carbon_factor_data_source: z.string().nullable(),
  additional_assumptions: z.string().nullable(),
});

const createSchema = (carbonFactorDataSource: string | null) => carbonFactorDataSource === 'Other'
  ? baseSchema.extend({
    other_carbon_factor_data_source: z.string({
      required_error: "Please specify the other carbon factor data source",
      invalid_type_error: "Please specify the other carbon factor data source"
    }).min(1, "Please specify the other carbon factor data source"),
  })
  : baseSchema;

const AddProjectSmqAssumptions = () => {
  const navigate = useNavigate();
  const { data: getMaterialQuantityDataSources = [] } = useGetMaterialQuantityDataSourcesQuery();
  const { data: carbonFactorDataSources = [] } = useGetCarbonFactorDataSourcesQuery();
  const { data: userData } = useGetUserQuery();

  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('ProjectContext error');
  }
  const { projectDetails } = context;

  const [schema, setSchema] = useState(() => createSchema(projectDetails.carbon_factor_data_source));

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      material_quantity_data_source: projectDetails.material_quantity_data_source || null,
      steel_connection_allowances: projectDetails.steel_connection_allowances || null,
      rebar_allowances: projectDetails.rebar_allowances || null,
      concrete_waste_allowances: projectDetails.concrete_waste_allowances || null,
      carbon_factor_data_source: projectDetails.carbon_factor_data_source || null,
      other_carbon_factor_data_source: projectDetails.other_carbon_factor_data_source || '',
      additional_assumptions: projectDetails.additional_assumptions || '',
    },
  });

  const { control, handleSubmit, getValues, reset, watch, trigger, formState: { errors, isSubmitting, isDirty } } = form;

  const carbonFactorDataSource = watch('carbon_factor_data_source');

  useEffect(() => {
    setSchema(createSchema(carbonFactorDataSource));
    if (carbonFactorDataSource !== 'Other') {
      // keepDirty: without it, reset() clears isDirty even when the user has unsaved
      // edits on this step, so Back would silently discard them instead of saving or
      // prompting (it would read as a clean, navigable form).
      reset({ ...getValues(), other_carbon_factor_data_source: '' }, { keepDirty: true });
    }
  }, [carbonFactorDataSource]);

  const { handleCreateUpdateProject, error: saveError, isLoading, revertPrompt } = useCreateUpdateProject({ userData })

  const listMaterialQuantityDataSources = getMaterialQuantityDataSources.map(item => item.name);
  const listCarbonFactorDataSources = carbonFactorDataSources.map(item => item.name);

  // Turns empty strings into null values (mainly to ensure empty strings aren't submitted as allowances to the backend)
  const buildFormValues = () => Object.fromEntries(
    Object.entries(getValues()).map(([key, value]) => [key, value === '' ? null : value])
  );

  const handleGotoNextForm = async () => {
    const transformedValues = buildFormValues();

    // Wait for the save and stay on the step if it failed - otherwise the user advances
    // believing the data was stored (ID 7).
    const saved = await handleCreateUpdateProject({
      formValues: transformedValues,
      leaveForm: false,
      fields: STEP_FIELDS[6],
    });

    if (!saved) return;

    navigate('/add-project/7');
  };

  const handleSaveAndExit = async () => {
    const transformedValues = buildFormValues();

    handleCreateUpdateProject({ formValues: transformedValues, fields: STEP_FIELDS[6] })
  };

  const { handleClickBack, showDiscardPrompt, cancelDiscard, confirmDiscard } = useStepBackNavigation({
    previousPath: '/add-project/5',
    fields: STEP_FIELDS[6],
    buildFormValues,
    isDirty,
    trigger,
    save: handleCreateUpdateProject,
  });

  return (
    <StyledContainer>
      <div style={{ width: '100%', height: '100%', background: 'white', boxShadow: '0px 1px 8px rgba(0, 0, 0, 0.12)', borderRadius: 5, padding: 20, paddingLeft: 41, paddingTop: 37 }}>
        <Box sx={{ width: "80vw", paddingLeft: ".5vw" }}>
          <div style={{ width: '100%', height: '100%', color: '#1A1A1A', fontSize: 28, fontFamily: 'Helvetica Neue LT Std', fontWeight: '300', wordWrap: 'break-word', paddingBottom: 12 }}>Structural Material Quantities Assumptions</div>
          <div style={{ width: '100%', height: '100%', color: '#1A1A1A', fontSize: 16, fontFamily: 'Helvetica Neue LT Std', fontStyle: 'italic', wordWrap: 'break-word', paddingBottom: 12 }}>
            We are looking to identify industry trends in assumptions around structural material quantities and structural embodied carbon calculations. <br /> Please provide your feedback on the following points.
          </div>
          <form onSubmit={handleSubmit(handleGotoNextForm)}>
            <div className="smq-assumptions-wrapper">
              <div className="column-one regular-text row-one row-title">
                What data source did you use as a basis for building up your structural material quantities?
              </div>
              <div className="column-one regular-text row-two row-title">
                <strong>Steel connection allowances [optional]:</strong> For steel elements, what percentage steel connection allowance by weight did you assume?
                (Leave blank if connections were not included or if actual connection quantities were known and reported)
              </div>
              <div className="column-one regular-text row-three row-title">
                <strong>Rebar allowances [optional]:</strong> For reinforced concrete elements, what percentage rebar allowance by weight did you assume for laps, splices, and hooks?
                (Leave blank if laps, splices, and hooks were not included or if actual rebar quantities were known and reported)
              </div>
              <div className="column-one regular-text row-four row-title">
                <strong>Concrete waste allowances [optional]:</strong> For ready-mix concrete elements what percentage waste allowance by volume did you assume?
                (Leave blank if concrete waste was not included or if actual concrete quantities were known and reported)
              </div>
              <div className="column-one regular-text row-five row-title">
                <strong>A1-A3 Overrides [optional]:</strong> If you entered custom A1-A3 carbon factors rather than using the default values for your structural
                material quantities inputs, please describe the data source you used to estimate these factors.
              </div>
              <div className="column-one regular-text row-six row-title">
                <strong>Additional Information [optional]:</strong> Are there other critical assumptions you made to develop structural materials quantities in order to
                perform your embodied carbon assessment? Please note that any notes you input here might be reported to others outside your firm -
                please avoid including any firm identifying information in these notes (including your Firm name)
              </div>

              <div className="column-two row-one">
                <Controller
                  name="material_quantity_data_source"
                  control={control}
                  defaultValue={projectDetails.material_quantity_data_source || undefined}
                  render={({ field }) => (
                    <Box>
                      <FormSelect
                        {...field}
                        label='Structural Material Quantities Data Source'
                        error={!!errors.material_quantity_data_source}
                        helperText={errors.material_quantity_data_source?.message}
                        menuItems={listMaterialQuantityDataSources}
                        disabled={isSubmitting}
                        updateFunction={(value) => field.onChange(value)}
                      />
                    </Box>
                  )}
                />
              </div>
              <div style={{ width: 100 }} className="column-two row-two">
                <Controller
                  name="steel_connection_allowances"
                  control={control}
                  defaultValue={projectDetails.steel_connection_allowances || undefined}
                  render={({ field }) => (
                    <Box sx={{ marginBottom: '5px' }}>
                      <FormLabeledTextField
                        {...field}
                        label='%'
                        error={!!errors.steel_connection_allowances}
                        helperText={errors.steel_connection_allowances?.message}
                        disabled={isSubmitting}
                        updateFunction={(value) => field.onChange(value)}
                      />
                    </Box>
                  )}
                />
              </div>
              <div style={{ width: 100 }} className="column-two row-three">
                <Controller
                  name="rebar_allowances"
                  control={control}
                  defaultValue={projectDetails.rebar_allowances || undefined}
                  render={({ field }) => (
                    <Box sx={{ marginBottom: '5px' }}>
                      <FormLabeledTextField
                        {...field}
                        label='%'
                        error={!!errors.rebar_allowances}
                        helperText={errors.rebar_allowances?.message}
                        disabled={isSubmitting}
                        updateFunction={(value) => field.onChange(value)}
                      />
                    </Box>
                  )}
                />
              </div>
              <div style={{ width: 100 }} className="column-two row-four">
                <Controller
                  name="concrete_waste_allowances"
                  control={control}
                  defaultValue={projectDetails.concrete_waste_allowances || undefined}
                  render={({ field }) => (
                    <Box sx={{ marginBottom: '5px' }}>
                      <FormLabeledTextField
                        {...field}
                        label='%'
                        error={!!errors.concrete_waste_allowances}
                        helperText={errors.concrete_waste_allowances?.message}
                        disabled={isSubmitting}
                        updateFunction={(value) => field.onChange(value)}
                      />
                    </Box>
                  )}
                />
              </div>
              <div className="column-two row-five" style={{ marginBottom: "1rem" }}>
                <Controller
                  name="carbon_factor_data_source"
                  control={control}
                  defaultValue={projectDetails.carbon_factor_data_source || undefined}
                  render={({ field }) => (
                    <Box>
                      <FormSelect
                        {...field}
                        label='Carbon Factor Data Source'
                        error={!!errors.carbon_factor_data_source}
                        helperText={errors.carbon_factor_data_source?.message}
                        menuItems={listCarbonFactorDataSources}
                        disabled={isSubmitting}
                        updateFunction={(value) => field.onChange(value)}
                      />
                    </Box>
                  )}
                />
                {carbonFactorDataSource === 'Other' && (
                  <Controller
                    name="other_carbon_factor_data_source"
                    control={control}
                    defaultValue={projectDetails.other_carbon_factor_data_source || undefined}
                    render={({ field }) => (
                      <Box sx={{ marginTop: 2 }}>
                        <FormTextField
                          {...field}
                          label="Specify Other Carbon Factor Data Source"
                          error={!!errors.other_carbon_factor_data_source}
                          helperText={errors.other_carbon_factor_data_source?.message}
                        />
                      </Box>
                    )}
                  />
                )}
              </div>
              <div className="column-two row-six" style={{ alignContent: 'top' }}>
                <Controller
                  name="additional_assumptions"
                  control={control}
                  defaultValue={projectDetails.additional_assumptions || undefined}
                  render={({ field }) => (
                    <Box sx={{ marginBottom: '5px', height: '100%' }}>
                      <FormTextArea
                        {...field}
                        label=''
                        error={!!errors.additional_assumptions}
                        helperText={errors.additional_assumptions?.message}
                        disabled={isSubmitting}
                        updateFunction={(value) => field.onChange(value)}
                      />
                    </Box>
                  )}
                />
              </div>
              <Box sx={{ display: "flex", mt: 3, justifyContent: "flex-start" }}>
                <StyledButton label="Save &amp; Exit" type="outlined" onClick={handleSubmit(handleSaveAndExit)} />
                <StyledButton label="Back" type="outlined" disabled={isSubmitting || isLoading} onClick={handleClickBack} />
                <StyledButton label="Next" type="filled" onClick={handleSubmit(handleGotoNextForm)} />
              </Box>
              <SaveErrorAlert error={saveError} />
              <RevertToDraftDialog {...revertPrompt} />
              <DiscardChangesDialog
                open={showDiscardPrompt}
                onCancel={cancelDiscard}
                onDiscard={confirmDiscard}
              />
            </div>
          </form>
        </Box>
      </div>
    </StyledContainer>
  );
};

const StyledContainer = styled(Container)({
  marginBottom: 24,
});

export default AddProjectSmqAssumptions;